import React from 'react';
import { useNavigate } from 'react-router-dom';

const services = [
  {
    title: 'Temporary Recruitment',
    icon: '⚡',
    desc: 'Fast, flexible staffing solutions for construction projects of any scale. Whether you need one skilled tradesperson or an entire site team, we deliver quality candidates when you need them most.',
    features: ['Same-day placements available', 'Pre-vetted, qualified candidates', 'CSCS card verification', 'Right-to-work checks', '24/7 emergency cover', 'Payroll management included'],
    roles: ['General Labourers', 'Skilled Tradespeople', 'Machine Operators', 'Site Supervisors', 'Health & Safety Officers'],
  },
  {
    title: 'Permanent Recruitment',
    icon: '🎯',
    desc: 'Strategic recruitment for career-defining roles. We take time to understand your company culture and project requirements to find candidates who will thrive and grow with your organization.',
    features: ['Detailed client consultation', 'Targeted candidate search', 'Thorough screening & interviews', 'Skills & qualification verification', 'Reference checking', 'Offer management & onboarding support'],
    roles: ['Project Managers', 'Site Managers', 'Quantity Surveyors', 'Engineers (Civil, Structural, M&E)', 'Estimators', 'Contracts Managers', 'Health & Safety Managers', 'Planners'],
  },
  {
    title: 'Contract Recruitment',
    icon: '📋',
    desc: 'Skilled professionals for fixed-term contracts and specific project phases. Ideal for seasonal peaks, maternity cover, or project-specific expertise.',
    features: ['Defined contract periods', 'Specialist skills on demand', 'Cost-effective solution', 'Reduced hiring risk', 'Quick mobilisation'],
    roles: [],
  },
  {
    title: 'Executive Search',
    icon: '🔍',
    desc: 'Headhunting services for senior leadership positions. Our extensive network and industry knowledge help you secure transformational talent.',
    features: ['Confidential headhunting', 'Market mapping', 'Succession planning', 'Extensive industry network'],
    roles: ['Construction Directors', 'Regional Managers', 'Operations Directors', 'Commercial Directors', 'Development Managers'],
  },
  {
    title: 'Multi-Hire Solutions',
    icon: '👥',
    desc: 'Assembling complete teams for large-scale projects. We coordinate multi-trade recruitment to ensure your project has the right people at the right time.',
    features: ['Complete team assembly', 'Multi-trade coordination', 'Phased recruitment programs', 'Nationwide deployment'],
    roles: ['New build developments', 'Infrastructure projects', 'Major refurbishments', 'Mixed-use developments', 'Industrial facilities'],
  },
];

export default function ServicesPage() {
  const navigate = useNavigate();

  return (
    <div>
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <h1>Our Services</h1>
          <p>Comprehensive recruitment solutions for the construction industry</p>
          <div className="breadcrumb">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <span>/</span>
            <span style={{ color: 'rgba(255,255,255,0.9)' }}>Services</span>
          </div>
        </div>
      </section>

      {/* Services */}
      {services.map((service, idx) => (
        <section className={idx % 2 === 0 ? 'section' : 'section-alt'} key={idx}>
          <div className="container">
            <div className="grid grid-2" style={{ alignItems: 'start', gap: '50px' }}>
              <div style={{ order: idx % 2 === 0 ? 1 : 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                  <div className="card-icon">{service.icon}</div>
                  <h2 style={{ margin: 0 }}>{service.title}</h2>
                </div>
                <p style={{ fontSize: '1.05rem', lineHeight: '1.8' }}>{service.desc}</p>
                {service.roles.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h4 style={{ marginBottom: '10px' }}>
                      {service.title === 'Multi-Hire Solutions' ? 'Project Types:' : 'Typical Roles:'}
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {service.roles.map((r, i) => (
                        <span className="badge badge-outline" key={i}>{r}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ order: idx % 2 === 0 ? 2 : 1 }}>
                <div className="card" style={{ borderLeft: '4px solid var(--accent)' }}>
                  <h4 style={{ marginBottom: '15px' }}>Key Features</h4>
                  <ul className="card-list">
                    {service.features.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Process */}
      <section className="section-dark">
        <div className="container">
          <div className="section-header">
            <h2>Our Recruitment Process</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)' }}>
              A streamlined approach to finding the right talent for your project.
            </p>
          </div>
          <div className="process-steps">
            {[
              { num: '1', title: 'Discovery', desc: 'Understand your needs' },
              { num: '2', title: 'Search', desc: 'Activate our network' },
              { num: '3', title: 'Screen', desc: 'Verify skills & compliance' },
              { num: '4', title: 'Present', desc: 'Shortlist candidates' },
              { num: '5', title: 'Interview', desc: 'Coordinate & assist' },
              { num: '6', title: 'Place', desc: 'Smooth onboarding' },
              { num: '7', title: 'Support', desc: 'Ongoing follow-up' },
              { num: '8', title: 'Review', desc: 'Feedback & improve' },
            ].map((step, i) => (
              <div className="process-step" key={i}>
                <div className="process-step-number">{step.num}</div>
                <h4 style={{ color: 'var(--white)' }}>{step.title}</h4>
                <p style={{ color: 'rgba(255,255,255,0.7)' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <h2>Need Construction Staff?</h2>
          <p>Tell us your requirements and we'll find the right people.</p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-lg" onClick={() => navigate('/employers')}>
              Request Staff
            </button>
            <button className="btn btn-dark btn-lg" onClick={() => navigate('/contact')}>
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
